import { formatDisplayEmail } from '@bs/core'
import { Button, pantryColors } from '@pantry/ui'
import React from 'react'
import type {
  FlowHistoryStatus,
  LoyaltyFlowHistoryEvent,
} from '../../../../types/loyaltyMarketing'
import { useLoyaltyFlowHistoryQuery } from '../../hooks/useLoyaltyMarketing'
import { ActivityMessage, ActivityRow } from './CampaignActivityList'
import { EMERALD } from './CampaignTypeIcon'

const p = pantryColors

export const formatWhen = (datetime?: string): string => {
  if (!datetime) return '—'
  const parsed = new Date(datetime)
  if (Number.isNaN(parsed.getTime())) return datetime
  return parsed.toLocaleString()
}

// sent = green, failed = red, scheduled/skipped = muted.
export const statusColor = (status: FlowHistoryStatus): string => {
  if (status === 'sent') return EMERALD
  if (status === 'failed') return p.red[700]
  return p.gray[400]
}

export const STATUS_TEXT: Record<FlowHistoryStatus, string> = {
  scheduled: 'Scheduled',
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
}

// Why a run stopped, in plain words. Unknown reasons (send errors) show as is.
const REASON_TEXT: Record<string, string> = {
  unsubscribed: 'Customer unsubscribed',
  converted: 'Customer placed an order',
  'cart unavailable': 'Cart no longer available',
  min_cart_value: 'Cart below the minimum value',
  'opened email': 'Customer opened an email',
  'visited site': 'Customer came back to the site',
  'sent recently': 'Within the frequency limit',
  'no recipient email': 'No email address',
}

export const reasonText = (reason?: string): string | undefined =>
  reason ? (REASON_TEXT[reason] ?? reason) : undefined

// "Sent · Email 2" (the email number only exists on abandoned-cart rows).
export const eventLabel = (event: LoyaltyFlowHistoryEvent): string =>
  `${STATUS_TEXT[event.status] ?? event.status}${
    event.email_number ? ` · Email ${event.email_number}` : ''
  }`

// Flow trigger log (scheduled → sent/failed/skipped per customer), newest
// first, "Load more" over the cursor feed. Skip reasons (converted,
// min_cart_value, unsubscribed — spec §4) show on the second line.
export const FlowActivityList: React.FC<{
  storeId: string
  flowId: string
  enabled?: boolean
}> = ({ storeId, flowId, enabled = true }) => {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLoyaltyFlowHistoryQuery(storeId, flowId, { enabled })
  const events = (data?.pages ?? []).flatMap((page) => page.events)

  if (isLoading)
    return <ActivityMessage loading>Loading history…</ActivityMessage>
  if (isError)
    return (
      <ActivityMessage>
        Couldn't load flow history. Please try again.
      </ActivityMessage>
    )
  if (events.length === 0)
    return <ActivityMessage>No triggers yet.</ActivityMessage>

  return (
    <div>
      {events.map((event) => (
        <ActivityRow
          key={event.id}
          color={statusColor(event.status)}
          text={`${eventLabel(event)} · ${
            formatDisplayEmail(event.email) || event.customer_id
          }`}
          sub={reasonText(event.reason)}
          when={formatWhen(event.created_at)}
        />
      ))}
      {hasNextPage && (
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}
        >
          <Button
            variant="ghost"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}
